/**
 * @component ry-select
 * @category  form
 * @summary   Custom dropdown select with keyboard nav, typeahead, and optional multi-select.
 *
 * @attribute value             string?                       Selected value (single) or comma-joined values (multiple).
 * @attribute placeholder       string?                       Text shown when nothing is selected.
 * @attribute name              string?                       Form field name on the inner native <select>.
 * @attribute disabled          boolean   default=false       Non-interactive.
 * @attribute multiple          boolean   default=false       Multi-select mode (tags).
 * @attribute clearable         boolean   default=false       Adds an "×" button to clear all values.
 * @attribute select-on-keydown boolean   default=true        Arrow keys commit selection live (Fomantic parity). Set to "false" to require Enter.
 * @attribute max-selections    number?                       Limit on multi-select.
 *
 * @property  value             string                        Single mode: selected value; multi mode: comma-joined values.
 * @property  values            string[]                      Multi mode only: array of selected values.
 *
 * @method    open()                                          Show the dropdown.
 * @method    close()                                         Hide the dropdown.
 * @method    toggle()                                        Toggle open/closed.
 *
 * @event ry:beforechange
 *        detail: { value: string, label: string, previous: string }
 *        fires:  before a commit lands — single select, multi toggle, or clear — with `value` the value
 *                ABOUT to be set (multi: the comma-joined next set) and `previous` the current `el.value`
 *        cancelable: preventDefault() leaves the selection as it was; `ry:change` does not fire
 * @event ry:change
 *        detail: { value: string, label: string }
 *        fires:  on commit (click option, Enter, or arrow key when select-on-keydown is true)
 * @event ry:beforeopen
 *        detail: void
 *        fires:  at the top of `open()` (click, keyboard, or programmatic), before any state change
 *        cancelable: preventDefault() keeps the dropdown closed; `ry:open` does not fire
 * @event ry:open
 *        detail: void
 *        fires:  dropdown opens
 * @event ry:beforeclose
 *        detail: void
 *        fires:  at the top of `close()` — commit, Escape, click outside, or `el.close()` — before any state change
 *        cancelable: preventDefault() keeps the dropdown open; `ry:close` does not fire
 * @event ry:close
 *        detail: void
 *        fires:  dropdown closes (commit, Escape, click outside)
 *
 * @keyboard Space/Enter — toggle dropdown when closed; commit highlighted when open.
 * @keyboard ArrowDown/ArrowUp — navigate options (commits if select-on-keydown is true).
 * @keyboard Home/End — jump to first/last option.
 * @keyboard Escape — close without commit.
 * @keyboard Any letter — typeahead match.
 * @keyboard Backspace (multi only) — remove last selected tag.
 *
 * @content  `<ry-option value="...">Label</ry-option>` children become the options.
 *
 * @state    open    — dropdown is visible
 * @state    closed  — dropdown is hidden (default)
 *
 * @dom
 *   ry-select [data-ry-state="open|closed"]
 *     > div.ry-select__trigger
 *       > span.ry-select__value | div.ry-select__tags (multi)
 *       > svg.ry-select__chevron
 *     > div.ry-select__dropdown
 *       > div[data-ry-target="option"][data-value="..."][data-highlighted?] × N
 *     > select.ry-select__native (hidden, for form submission)
 *
 * @gotcha The option list rendered inside the dropdown stores each value on
 *         `data-value`, NOT on a `value` attribute. Read with `el.dataset.value`.
 *
 * @gotcha When `multiple` is set, `el.value` is a comma-joined string. Use
 *         `el.values` (array) for programmatic access.
 *
 * @example simple
 * ```html
 * <ry-select placeholder="Choose a country" name="country">
 *   <ry-option value="us">United States</ry-option>
 *   <ry-option value="uk">United Kingdom</ry-option>
 *   <ry-option value="ca" disabled>Canada</ry-option>
 * </ry-select>
 * ```
 *
 * @example pre-selected
 * ```html
 * <ry-select value="uk">
 *   <ry-option value="us">United States</ry-option>
 *   <ry-option value="uk">United Kingdom</ry-option>
 * </ry-select>
 * ```
 *
 * @example multi
 * ```html
 * <ry-select multiple clearable placeholder="Pick fruits">
 *   <ry-option value="apple">Apple</ry-option>
 *   <ry-option value="pear">Pear</ry-option>
 *   <ry-option value="kiwi">Kiwi</ry-option>
 * </ry-select>
 * ```
 *
 * @example clearable
 * ```html
 * <ry-select clearable placeholder="Choose (clearable)">
 *   <ry-option value="us">United States</ry-option>
 *   <ry-option value="uk">United Kingdom</ry-option>
 * </ry-select>
 * ```
 *
 * @example with-disabled-options
 * ```html
 * <ry-select placeholder="Pick a plan">
 *   <ry-option value="free">Free</ry-option>
 *   <ry-option value="pro">Pro</ry-option>
 *   <ry-option value="ent" disabled>Enterprise (contact sales)</ry-option>
 * </ry-select>
 * ```
 *
 * @example disabled-control
 * ```html
 * <ry-select disabled value="us">
 *   <ry-option value="us">United States</ry-option>
 *   <ry-option value="uk">United Kingdom</ry-option>
 * </ry-select>
 * ```
 *
 * @example scrolling-long-list
 * ```html
 * <ry-select placeholder="Pick a number">
 *   <ry-option value="1">One</ry-option>
 *   <ry-option value="2">Two</ry-option>
 *   <ry-option value="3">Three</ry-option>
 *   <ry-option value="4">Four</ry-option>
 *   <ry-option value="5">Five</ry-option>
 *   <ry-option value="6">Six</ry-option>
 *   <ry-option value="7">Seven</ry-option>
 *   <ry-option value="8">Eight</ry-option>
 *   <ry-option value="9">Nine</ry-option>
 *   <ry-option value="10">Ten</ry-option>
 * </ry-select>
 * ```
 *
 * @example multi-pre-selected
 * ```html
 * <ry-select multiple value="apple,kiwi" placeholder="Pick fruits">
 *   <ry-option value="apple">Apple</ry-option>
 *   <ry-option value="pear">Pear</ry-option>
 *   <ry-option value="kiwi">Kiwi</ry-option>
 *   <ry-option value="banana">Banana</ry-option>
 * </ry-select>
 * ```
 *
 * @example required-form-field
 * ```html
 * <ry-field label="Country" error="Required">
 *   <ry-select name="country" required>
 *     <ry-option value="">— select —</ry-option>
 *     <ry-option value="us">United States</ry-option>
 *     <ry-option value="uk">United Kingdom</ry-option>
 *   </ry-select>
 * </ry-field>
 * ```
 *
 * @tutorial basic
 * ```js
 * const el = mount(`
 *   <ry-select placeholder="Choose">
 *     <ry-option value="a">Alpha</ry-option>
 *     <ry-option value="b">Beta</ry-option>
 *     <ry-option value="c">Gamma</ry-option>
 *   </ry-select>
 * `);
 * await expectEqual(el.value, '', 'starts empty');
 *
 * // Programmatically open and commit the second option
 * el.open();
 * await expectEqual(el.state, 'open', 'open() flips state');
 *
 * const options = el.querySelectorAll('[data-ry-target="option"]');
 * await expectEqual(options.length, 3, 'option list rendered');
 *
 * // Click the second option to select it
 * await expectEvent(el, 'ry:change', () => options[1].click(), { value: 'b' });
 * await expectEqual(el.value, 'b', 'value reflects selection');
 * await expectEqual(el.state, 'closed', 'dropdown closes after select');
 * ```
 *
 * @tutorial select-on-keydown
 * ```js
 * // Default behavior (Fomantic-style): arrow keys commit live as the user navigates.
 * const el = mount(`
 *   <ry-select>
 *     <ry-option value="a">A</ry-option>
 *     <ry-option value="b">B</ry-option>
 *     <ry-option value="c">C</ry-option>
 *   </ry-select>
 * `);
 * el.open();
 * // Opening highlights option index 0 ("a") visually but does NOT commit it
 * // (selection is empty until the user actually moves or chooses).
 * await expectEqual(el.value, '', 'open does not commit a value');
 *
 * // ArrowDown advances highlight from 0 -> 1 and commits 'b' live.
 * await expectEvent(el, 'ry:change', () => {
 *   el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
 * }, { value: 'b' });
 * await expectEqual(el.value, 'b', 'first ArrowDown advances to b and commits');
 *
 * // Next ArrowDown -> c
 * await expectEvent(el, 'ry:change', () => {
 *   el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true }));
 * }, { value: 'c' });
 * await expectEqual(el.value, 'c', 'second ArrowDown advances to c');
 *
 * // ArrowUp -> back to b
 * await expectEvent(el, 'ry:change', () => {
 *   el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowUp', bubbles: true }));
 * }, { value: 'b' });
 * ```
 *
 * @tutorial escape-closes-without-commit
 * ```js
 * const el = mount(`
 *   <ry-select>
 *     <ry-option value="a">A</ry-option>
 *     <ry-option value="b">B</ry-option>
 *   </ry-select>
 * `);
 * el.open();
 * await expectEqual(el.state, 'open');
 *
 * // Press Escape — dropdown closes, value stays empty (no commit).
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
 * await expectEqual(el.state, 'closed', 'Escape closes the dropdown');
 * await expectEqual(el.value, '', 'Escape does not commit a value');
 * ```
 *
 * @tutorial typeahead
 * ```js
 * // Pressing a letter while open jumps the highlight to the first option
 * // whose label starts with that letter. With select-on-keydown (default),
 * // that also commits the selection — symmetric with ArrowUp/Down behavior.
 * const el = mount(`
 *   <ry-select>
 *     <ry-option value="apple">Apple</ry-option>
 *     <ry-option value="banana">Banana</ry-option>
 *     <ry-option value="cherry">Cherry</ry-option>
 *   </ry-select>
 * `);
 * el.open();
 *
 * // Type 'c' — jumps to Cherry AND commits.
 * await expectEvent(el, 'ry:change', () => {
 *   el.dispatchEvent(new KeyboardEvent('keydown', { key: 'c', bubbles: true }));
 * }, { value: 'cherry' });
 * await expectEqual(
 *   el.querySelector('[data-highlighted]')?.dataset.value,
 *   'cherry',
 *   'typeahead jumped to cherry',
 * );
 * await expectEqual(el.value, 'cherry', 'typeahead also committed');
 * ```
 *
 * @tutorial multi-select
 * ```js
 * const el = mount(`
 *   <ry-select multiple placeholder="Pick">
 *     <ry-option value="apple">Apple</ry-option>
 *     <ry-option value="banana">Banana</ry-option>
 *     <ry-option value="cherry">Cherry</ry-option>
 *   </ry-select>
 * `);
 * await expectEqual(el.values?.length ?? 0, 0, 'starts with no selections');
 *
 * el.open();
 * const options = el.querySelectorAll('[data-ry-target="option"]');
 *
 * // Click two options — they accumulate.
 * await expectEvent(el, 'ry:change', () => options[0].click(), { value: 'apple' });
 * await expectEvent(el, 'ry:change', () => options[2].click(), { value: 'apple,cherry' });
 * await expectEqual(el.value, 'apple,cherry', 'value is comma-joined');
 *
 * // Clicking an already-selected option toggles it off.
 * await expectEvent(el, 'ry:change', () => options[0].click(), { value: 'cherry' });
 * await expectEqual(el.value, 'cherry');
 * ```
 *
 * @tutorial clearable
 * ```js
 * // The clearable attribute shows an "x" button that empties all selections.
 * // Most useful with multiple; works for single-mode too when the button is present.
 * const el = mount(`
 *   <ry-select multiple clearable>
 *     <ry-option value="a">A</ry-option>
 *     <ry-option value="b">B</ry-option>
 *   </ry-select>
 * `);
 * el.open();
 * const options = el.querySelectorAll('[data-ry-target="option"]');
 * await expectEvent(el, 'ry:change', () => options[0].click(), { value: 'a' });
 *
 * const clearBtn = el.querySelector('[data-ry-target="clear"]');
 * await expectEqual(!!clearBtn, true, 'clearable shows the clear button');
 * await expectEvent(el, 'ry:change', () => clearBtn.click(), { value: '' });
 * await expectEqual(el.value, '', 'clear button empties the selection');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange leaves the selection untouched and
 * // ry:change stays silent — the "confirm before switching" guard.
 * const el = mount(`
 *   <ry-select>
 *     <ry-option value="a">A</ry-option>
 *     <ry-option value="b">B</ry-option>
 *   </ry-select>
 * `);
 * const options = el.querySelectorAll('[data-ry-target="option"]');
 * el.open();
 * const changed = [];
 * el.addEventListener('ry:change', () => changed.push(1));
 * el.addEventListener('ry:beforechange', (e) => {
 *   // detail carries what is about to happen + what it is now
 *   if (e.detail.value === 'b' && e.detail.previous === '') e.preventDefault();
 * }, { once: true });
 * options[1].click();
 * await expectEqual(el.value, '', 'vetoed commit leaves the value empty');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * el.open();
 * options[1].click();
 * await expectEqual(el.value, 'b', 'without the veto, the commit lands');
 * await expectEqual(changed.length, 1, 'ry:change fired once');
 * ```
 *
 * @tutorial beforeopen-veto
 * ```js
 * // preventDefault() on ry:beforeopen / ry:beforeclose pins the dropdown.
 * const el = mount(`
 *   <ry-select>
 *     <ry-option value="a">A</ry-option>
 *   </ry-select>
 * `);
 * const opened = [];
 * el.addEventListener('ry:open', () => opened.push(1));
 * el.addEventListener('ry:beforeopen', (e) => e.preventDefault(), { once: true });
 * el.open();
 * await expectEqual(el.state, 'closed', 'vetoed open stays closed');
 * await expectEqual(opened.length, 0, 'ry:open did not fire');
 * el.open();
 * await expectEqual(el.state, 'open', 'without the veto, open() works');
 *
 * const closed = [];
 * el.addEventListener('ry:close', () => closed.push(1));
 * el.addEventListener('ry:beforeclose', (e) => e.preventDefault(), { once: true });
 * el.close();
 * await expectEqual(el.state, 'open', 'vetoed close stays open');
 * await expectEqual(closed.length, 0, 'ry:close did not fire');
 * el.close();
 * await expectEqual(el.state, 'closed', 'without the veto, close() works');
 * ```
 *
 * @chain ry-option
 * ```js
 * // ry-option is a passive child — ry-select reads its value+text on setup,
 * // renders the option markup into the dropdown panel, and never re-reads.
 * // Adding/removing ry-option children after setup is a known limitation.
 * const el = mount(`
 *   <ry-select>
 *     <ry-option value="x">First</ry-option>
 *     <ry-option value="y" disabled>Second (disabled)</ry-option>
 *   </ry-select>
 * `);
 * const opts = el.querySelectorAll('[data-ry-target="option"]');
 * await expectEqual(opts.length, 2, 'rendered options match ry-option children');
 * await expectEqual(opts[1].hasAttribute('data-disabled'), true, 'disabled attr forwards');
 * ```
 *
 * @see ry-combobox (searchable), ry-toggle-button (segmented, ≤4 options), ry-radio (small visible set)
 */

import { RyElement } from '../core/ry-element.js';
import { attachFloating } from '../core/ry-floating.js';
import { getIcon } from '../core/ry-icons.js';
import type { SelectOption, ChangeDetail, BeforeChangeDetail } from '../types.js';

let selectIdCounter = 0;

export class RySelect extends RyElement {
  #selectId: string | null = null;
  #highlightedIndex = -1;
  #typeahead = '';
  #typeaheadTimeout: ReturnType<typeof setTimeout> | null = null;
  #selectedValues: Set<string> = new Set();
  #floatingCleanup: (() => void) | null = null;
  #dropdownEl: HTMLElement | null = null;
  #portalRestore: (() => void) | null = null;

  // Store options reference
  _options: SelectOption[] = [];

  get #isMultiple(): boolean {
    return this.hasAttribute('multiple');
  }

  static observedAttributes = ['value', 'disabled', 'select-on-keydown'] as const;

  setup(): void {
    this.#selectId = `ry-select-${++selectIdCounter}`;
    this.#transformMarkup();

    // Capture the dropdown reference up-front. After open() it portals to
    // <body>, but the reference stays valid — all option queries route
    // through it via the #scope() helper, so they work in both states.
    this.#dropdownEl = this.$<HTMLElement>('[data-ry-target="dropdown"]');

    // Make focusable
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0');
    }

    // Events. Click listener is on BOTH the host (for trigger clicks) and
    // the dropdown (for option clicks — the dropdown portals to body, so
    // clicks inside it don't bubble through the host).
    this.on(this, 'click', this.#handleClick);
    this.on(this, 'keydown', this.#handleKeydown);
    if (this.#dropdownEl) {
      this.on(this.#dropdownEl, 'click', this.#handleClick);
    }
    this.on(document, 'click', this.#handleOutsideClick);

    // Listen for tag removal in multi-select
    if (this.#isMultiple) {
      this.on(this, 'ry:remove', this.#handleTagRemove as EventListener);
    }

    // Initialize state
    if (!this.hasAttribute('data-ry-state')) {
      this.state = 'closed';
    }

    // Set initial value
    const initialValue = this.getAttribute('value');
    if (initialValue) {
      if (this.#isMultiple) {
        for (const v of initialValue.split(',').map(s => s.trim()).filter(Boolean)) {
          this.#toggleMultiOption(v);
        }
      } else {
        this.value = initialValue;
      }
    }
  }

  #transformMarkup(): void {
    // Collect options from ry-option children
    const options: SelectOption[] = [...this.querySelectorAll('ry-option')].map(opt => ({
      value: opt.getAttribute('value') ?? opt.textContent?.trim() ?? '',
      label: opt.textContent?.trim() ?? '',
      disabled: opt.hasAttribute('disabled')
    }));

    const placeholder = this.getAttribute('placeholder') ?? 'Select...';
    const name = this.getAttribute('name') ?? '';
    const disabled = this.hasAttribute('disabled');
    const isMultiple = this.#isMultiple;

    // Build native select for form submission
    const nativeOptions = options.map(opt =>
      `<option value="${opt.value}"${opt.disabled ? ' disabled' : ''}>${opt.label}</option>`
    ).join('');

    // Build custom dropdown with checkmarks for multi-select
    const listboxOptions = options.map((opt, i) => `
      <div data-ry-target="option" class="ry-select__option"
           role="option"
           id="${this.#selectId}-option-${i}"
           data-value="${opt.value}"
           ${opt.disabled ? 'data-disabled' : ''}
           aria-disabled="${opt.disabled}">
        ${isMultiple ? `<span data-ry-target="check" class="ry-select__check">${getIcon('check')}</span>` : ''}
        <span class="ry-select__option-label">${opt.label}</span>
      </div>
    `).join('');

    if (isMultiple) {
      const clearable = this.hasAttribute('clearable');
      this.innerHTML = `
        <div data-ry-target="trigger" class="ry-select__trigger ry-select__trigger--multi" aria-haspopup="listbox" aria-expanded="false">
          <span data-ry-target="value" class="ry-select__value">${placeholder}</span>
          <div data-ry-target="tags" class="ry-select__tags"></div>
          ${clearable ? `<button data-ry-target="clear" class="ry-select__clear" aria-label="Clear all" type="button">${getIcon('close')}</button>` : ''}
          <span data-ry-target="arrow" class="ry-select__arrow">▾</span>
        </div>
        <div data-ry-target="dropdown" class="ry-select__dropdown ry-select__dropdown--multi" role="listbox" id="${this.#selectId}-listbox" tabindex="-1" aria-multiselectable="true">
          ${listboxOptions}
        </div>
        <select data-ry-target="native" class="ry-select__native" ${name ? `name="${name}"` : ''} multiple tabindex="-1" aria-hidden="true" ${disabled ? 'disabled' : ''}>
          ${nativeOptions}
        </select>
      `;

      // Clear button
      const clearBtn = this.$('[data-ry-target="clear"]');
      if (clearBtn) {
        this.on(clearBtn, 'click', (e: MouseEvent) => {
          e.stopPropagation();
          this.#clearAll();
        });
      }
    } else {
      this.innerHTML = `
        <div data-ry-target="trigger" class="ry-select__trigger" aria-haspopup="listbox" aria-expanded="false">
          <span data-ry-target="value" class="ry-select__value">${placeholder}</span>
          <span data-ry-target="arrow" class="ry-select__arrow">▾</span>
        </div>
        <div data-ry-target="dropdown" class="ry-select__dropdown" role="listbox" id="${this.#selectId}-listbox" tabindex="-1">
          ${listboxOptions}
        </div>
        <select data-ry-target="native" class="ry-select__native" ${name ? `name="${name}"` : ''} tabindex="-1" aria-hidden="true" ${disabled ? 'disabled' : ''}>
          <option value="">${placeholder}</option>
          ${nativeOptions}
        </select>
      `;
    }

    // Store options reference
    this._options = options;
  }

  /**
   * Query scope for option elements. After open() the dropdown portals to
   * <body>, so `this.$$` no longer finds options. The dropdown ref captured
   * in setup() always works.
   */
  #scope(): Element {
    return this.#dropdownEl ?? this;
  }

  #handleClick = (event: MouseEvent): void => {
    if (this.hasAttribute('disabled')) return;

    const target = event.target as Element;
    const option = target.closest('[data-ry-target="option"]');
    if (option instanceof HTMLElement && !option.hasAttribute('data-disabled')) {
      if (this.#isMultiple) {
        const val = option.dataset.value ?? '';
        this.#toggleMultiOption(val);
        // Don't close in multi-select
      } else {
        this.#selectOption(option);
        this.close();
      }
      return;
    }

    const trigger = target.closest('[data-ry-target="trigger"]');
    if (trigger) {
      this.toggle();
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (this.hasAttribute('disabled')) return;

    const isOpen = this.state === 'open';

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isOpen && this.#highlightedIndex >= 0) {
          const options = this.#scope().querySelectorAll<HTMLElement>('[data-ry-target="option"]:not([data-disabled])');
          const option = options[this.#highlightedIndex];
          if (option) {
            if (this.#isMultiple) {
              const val = option.dataset.value ?? '';
              this.#toggleMultiOption(val);
            } else {
              this.#selectOption(option);
              this.close();
            }
          }
        } else {
          this.toggle();
        }
        break;

      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          this.close();
        }
        break;

      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          this.open();
        } else {
          this.#highlightNext();
          this.#commitHighlightIfKeydown();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          this.#highlightPrev();
          this.#commitHighlightIfKeydown();
        }
        break;

      case 'Home':
        if (isOpen) {
          event.preventDefault();
          this.#highlightFirst();
          this.#commitHighlightIfKeydown();
        }
        break;

      case 'End':
        if (isOpen) {
          event.preventDefault();
          this.#highlightLast();
          this.#commitHighlightIfKeydown();
        }
        break;

      case 'Backspace':
        if (this.#isMultiple && this.#selectedValues.size > 0) {
          const vals = [...this.#selectedValues];
          const last = vals[vals.length - 1];
          if (last) {
            this.#toggleMultiOption(last);
          }
        }
        break;

      default:
        // Typeahead
        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
          this.#handleTypeahead(event.key);
        }
        break;
    }
  };

  #handleOutsideClick = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (this.state !== 'open') return;
    if (this.contains(target)) return;
    // Dropdown is portalled to <body>; clicks inside it count as inside.
    if (this.#dropdownEl && this.#dropdownEl.contains(target)) return;
    this.close();
  };

  #handleTagRemove = (e: CustomEvent): void => {
    const value = (e.detail as { value: string }).value;
    if (this.#selectedValues.has(value)) {
      this.#toggleMultiOption(value);
    }
  };

  #handleTypeahead(char: string): void {
    // Clear timeout
    if (this.#typeaheadTimeout) {
      clearTimeout(this.#typeaheadTimeout);
    }

    this.#typeahead += char.toLowerCase();

    // Find matching option
    const options = Array.from(this.#scope().querySelectorAll<HTMLElement>('[data-ry-target="option"]:not([data-disabled])'));
    const match = options.findIndex(opt =>
      opt.textContent?.trim().toLowerCase().startsWith(this.#typeahead)
    );

    if (match >= 0) {
      if (this.state === 'open') {
        this.#highlightIndex(match);
        // Symmetric with ArrowUp/Down: when select-on-keydown is the default
        // and we're in single mode, typeahead also commits the highlighted
        // option live. Skipped for multi-select to avoid clobbering picks.
        this.#commitHighlightIfKeydown();
      } else if (!this.#isMultiple) {
        const option = options[match];
        if (option) {
          this.#selectOption(option);
        }
      }
    }

    // Clear typeahead after 500ms
    this.#typeaheadTimeout = setTimeout(() => {
      this.#typeahead = '';
    }, 500);
  }

  #highlightIndex(index: number): void {
    const options = this.#scope().querySelectorAll<HTMLElement>('[data-ry-target="option"]:not([data-disabled])');
    if (index < 0 || index >= options.length) return;

    // Remove previous highlight
    this.#scope().querySelectorAll('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });

    // Add new highlight
    const option = options[index];
    if (!option) return;

    option.setAttribute('data-highlighted', '');
    // Scroll WITHIN the dropdown only — don't use scrollIntoView which walks
    // all ancestors. When the dropdown is portalled to <body> and FUI hasn't
    // yet written coords, the option sits at body top:0 and scrollIntoView
    // would scroll the page to top. Computing dropdown.scrollTop directly
    // affects only the dropdown's own overflow.
    if (this.#dropdownEl) {
      const optTop = option.offsetTop;
      const optHeight = option.offsetHeight;
      const scrollTop = this.#dropdownEl.scrollTop;
      const visibleHeight = this.#dropdownEl.clientHeight;
      if (optTop < scrollTop) {
        this.#dropdownEl.scrollTop = optTop;
      } else if (optTop + optHeight > scrollTop + visibleHeight) {
        this.#dropdownEl.scrollTop = optTop + optHeight - visibleHeight;
      }
    }
    this.#highlightedIndex = index;

    // Update aria-activedescendant
    const trigger = this.$('[data-ry-target="trigger"]');
    if (trigger) {
      trigger.setAttribute('aria-activedescendant', option.id);
    }
  }

  #highlightNext(): void {
    const options = this.#scope().querySelectorAll('[data-ry-target="option"]:not([data-disabled])');
    const next = Math.min(this.#highlightedIndex + 1, options.length - 1);
    this.#highlightIndex(next);
  }

  #highlightPrev(): void {
    const prev = Math.max(this.#highlightedIndex - 1, 0);
    this.#highlightIndex(prev);
  }

  #highlightFirst(): void {
    this.#highlightIndex(0);
  }

  /**
   * After arrow-key navigation, commit the highlighted option as the current
   * selection without closing the dropdown. Mirrors Fomantic UI's
   * `selectOnKeydown: true` default — the user sees the selection update
   * live as they navigate, instead of having to press Enter on each option.
   *
   * Opt out per-component with `select-on-keydown="false"`.
   * Multi-select is excluded (matching Fomantic) — toggling on every arrow
   * would be confusing.
   */
  #commitHighlightIfKeydown(): void {
    if (this.#isMultiple) return;
    if (this.getAttribute('select-on-keydown') === 'false') return;
    if (this.#highlightedIndex < 0) return;
    const options = this.#scope().querySelectorAll<HTMLElement>('[data-ry-target="option"]:not([data-disabled])');
    const option = options[this.#highlightedIndex];
    if (!option) return;
    // Skip re-selecting the same value (avoids redundant ry:change events
    // when wrapping at the edge of the list)
    const currentValue = this.getAttribute('value') ?? '';
    const nextValue = option.dataset.value ?? '';
    if (nextValue === currentValue) return;
    this.#selectOption(option);
  }

  #highlightLast(): void {
    const options = this.#scope().querySelectorAll('[data-ry-target="option"]:not([data-disabled])');
    this.#highlightIndex(options.length - 1);
  }

  // --- Multi-select methods ---

  #toggleMultiOption(val: string): void {
    const maxSelections = this.getAttribute('max-selections');
    // Compute the next set first so the before-event can describe (and veto)
    // it before anything is mutated.
    const next = new Set(this.#selectedValues);
    if (next.has(val)) {
      next.delete(val);
    } else {
      if (maxSelections && next.size >= parseInt(maxSelections, 10)) return;
      next.add(val);
    }
    const previous = this.value;
    const label = this._options.find(o => o.value === val)?.label ?? '';
    if (!this.emit<BeforeChangeDetail>('beforechange', { value: [...next].join(','), label, previous })) return;
    this.#selectedValues = next;
    this.#renderTags();
    this.#syncMultiNative();
    this.#updateMultiCheckmarks();
    this.setAttribute('value', [...this.#selectedValues].join(','));
    this.emit<ChangeDetail>('change', { value: this.value, label: '' });
  }

  #renderTags(): void {
    const tagsContainer = this.$('[data-ry-target="tags"]');
    const valueEl = this.$('[data-ry-target="value"]');
    if (!tagsContainer) return;

    tagsContainer.innerHTML = '';
    for (const val of this.#selectedValues) {
      const opt = this._options.find(o => o.value === val);
      if (!opt) continue;
      const tag = document.createElement('ry-tag');
      tag.setAttribute('removable', '');
      tag.setAttribute('size', 'sm');
      tag.setAttribute('data-value', val);
      tag.textContent = opt.label;
      tagsContainer.appendChild(tag);
    }

    // Show/hide placeholder
    if (valueEl) {
      if (this.#selectedValues.size > 0) {
        (valueEl as HTMLElement).style.display = 'none';
      } else {
        (valueEl as HTMLElement).style.display = '';
      }
    }
  }

  #syncMultiNative(): void {
    const nativeSelect = this.$<HTMLSelectElement>('[data-ry-target="native"]');
    if (!nativeSelect) return;
    for (const opt of nativeSelect.options) {
      opt.selected = this.#selectedValues.has(opt.value);
    }
  }

  #updateMultiCheckmarks(): void {
    this.#scope().querySelectorAll('[data-ry-target="option"]').forEach(optEl => {
      const val = (optEl as HTMLElement).dataset.value ?? '';
      if (this.#selectedValues.has(val)) {
        optEl.setAttribute('aria-selected', 'true');
      } else {
        optEl.removeAttribute('aria-selected');
      }
    });
  }

  #clearAll(): void {
    if (!this.emit<BeforeChangeDetail>('beforechange', { value: '', label: '', previous: this.value })) return;
    this.#selectedValues.clear();
    this.#renderTags();
    this.#syncMultiNative();
    this.#updateMultiCheckmarks();
    this.setAttribute('value', '');
    this.emit<ChangeDetail>('change', { value: '', label: '' });
  }

  // --- Single-select method ---

  #selectOption(optionEl: HTMLElement): void {
    const value = optionEl.dataset.value ?? '';
    const label = optionEl.textContent?.trim() ?? '';

    if (!this.emit<BeforeChangeDetail>('beforechange', { value, label, previous: this.value })) return;

    // Update display
    const valueEl = this.$('[data-ry-target="value"]');
    if (valueEl) {
      valueEl.textContent = label;
      valueEl.setAttribute('data-selected', '');
    }

    // Update native select
    const nativeSelect = this.$<HTMLSelectElement>('[data-ry-target="native"]');
    if (nativeSelect) {
      nativeSelect.value = value;
    }

    // Update attribute
    this.setAttribute('value', value);

    // Update selected state
    this.#scope().querySelectorAll('[data-ry-target="option"]').forEach(opt => {
      opt.removeAttribute('aria-selected');
    });
    optionEl.setAttribute('aria-selected', 'true');

    this.emit<ChangeDetail>('change', { value, label });
  }

  open(): void {
    if (this.state === 'open') return;
    if (!this.emit('beforeopen')) return;

    this.state = 'open';

    const trigger = this.$('[data-ry-target="trigger"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Floating UI positioning with matchWidth + flip + autoUpdate
    this.#attachFloating();

    // Highlight current value or first option
    if (!this.#isMultiple) {
      const currentValue = this.getAttribute('value');
      if (currentValue) {
        const options = Array.from(this.#scope().querySelectorAll<HTMLElement>('[data-ry-target="option"]:not([data-disabled])'));
        const index = options.findIndex(opt => opt.dataset.value === currentValue);
        if (index >= 0) {
          this.#highlightIndex(index);
        }
      } else {
        this.#highlightIndex(0);
      }
    } else {
      this.#highlightIndex(0);
    }

    this.emit('open');
  }

  #attachFloating(): void {
    const trigger = this.$<HTMLElement>('[data-ry-target="trigger"]');
    if (!this.#dropdownEl) {
      this.#dropdownEl = this.$<HTMLElement>('[data-ry-target="dropdown"]');
    }
    const dropdown = this.#dropdownEl;
    if (!trigger || !dropdown) return;

    // Portal dropdown to <body>. Tests must query from #dropdownEl since the
    // dropdown is no longer a descendant of `this`. State is mirrored onto
    // the dropdown itself so CSS visibility selectors (`.ry-select__dropdown
    // [data-ry-state="open"]`) keep working after portal.
    dropdown.setAttribute('data-ry-state', 'open');
    this.#portalRestore = this.portalToBody(dropdown);

    this.#floatingCleanup = attachFloating(trigger, dropdown, {
      placement: 'bottom-start',
      offset: 4,
      flip: true,
      shift: true,
      matchWidth: true,
      reflectPlacementAttribute: true,
    });
  }

  #detachFloating(): void {
    this.#floatingCleanup?.();
    this.#floatingCleanup = null;
    if (this.#dropdownEl) {
      this.#dropdownEl.setAttribute('data-ry-state', 'closed');
    }
    this.#portalRestore?.();
    this.#portalRestore = null;
  }

  close(): void {
    if (this.state === 'closed') return;
    if (!this.emit('beforeclose')) return;

    this.state = 'closed';

    const trigger = this.$('[data-ry-target="trigger"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
    }

    // Detach FUI
    this.#detachFloating();

    // Clear highlight
    this.#scope().querySelectorAll('[data-ry-target="option"][data-highlighted]').forEach(opt => {
      opt.removeAttribute('data-highlighted');
    });
    this.#highlightedIndex = -1;

    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }

  get value(): string {
    if (this.#isMultiple) {
      return [...this.#selectedValues].join(',');
    }
    return this.getAttribute('value') ?? '';
  }

  set value(val: string) {
    if (this.#isMultiple) {
      this.#selectedValues.clear();
      for (const v of val.split(',').map(s => s.trim()).filter(Boolean)) {
        this.#selectedValues.add(v);
      }
      this.#renderTags();
      this.#syncMultiNative();
      this.#updateMultiCheckmarks();
      this.setAttribute('value', val);
    } else {
      const option = this.#scope().querySelector<HTMLElement>(`[data-ry-target="option"][data-value="${val}"]`);
      if (option) {
        this.#selectOption(option);
      }
    }
  }

  get values(): string[] {
    return [...this.#selectedValues];
  }

  teardown(): void {
    if (this.#typeaheadTimeout) {
      clearTimeout(this.#typeaheadTimeout);
    }
  }
}

// Register placeholder element
class RyOption extends HTMLElement {}
customElements.define('ry-option', RyOption);

customElements.define('ry-select', RySelect);
